import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useProjects, useUsers, type ReportScope } from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { MultiSelect } from "@/components/common/MultiSelect";
import { NativeSelect } from "@/components/ui/input";
import { useScopeTeams } from "@/features/activity/TeamSelect";
import { dateInZone } from "@/lib/format";
import { periodRange, presetPeriods, validPeriod, type PresetPeriod } from "@/lib/periods";

const periods = ["week", "lastWeek", "month", "lastMonth", "today", "yesterday", "custom"] as const;
export type Period = PresetPeriod | "custom";

/** Report filters kept in the URL, so that a report can be shared by a link. */
// eslint-disable-next-line react-refresh/only-export-components
export function useReportFilters() {
  const me = useCurrentUser();
  const staff = isStaff(me);
  const [params, setParams] = useSearchParams();
  const today = dateInZone(new Date(), me.timezone);
  const raw = params.get("period");
  // A link with dates only (the weekly digest) means a custom period.
  const period: Period = (presetPeriods as readonly string[]).includes(raw ?? "")
    ? (raw as PresetPeriod)
    : raw === "custom" || params.has("from")
      ? "custom"
      : "month";
  const [from, to] =
    period === "custom"
      ? [params.get("from") ?? today, params.get("to") ?? params.get("from") ?? today]
      : periodRange(period, today);
  const scope: ReportScope = {
    from,
    to,
    teamId: (staff && params.get("team")) || undefined,
    userIds: staff ? params.getAll("user") : [],
    projectIds: params.getAll("project"),
  };

  const set = (changes: Record<string, string | string[] | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      p.delete(k);
      for (const item of Array.isArray(v) ? v : v ? [v] : []) p.append(k, item);
    }
    setParams(p, { replace: true });
  };

  return { period, today, scope, valid: validPeriod(from, to), staff, params, set };
}

export type ReportFilters = ReturnType<typeof useReportFilters>;

type FiltersBarProps = {
  filters: ReportFilters;
  /** The report can be narrowed to projects. */
  projects: boolean;
};

/** Period, team, people and projects of a report. */
export function FiltersBar({ filters, projects: withProjects }: FiltersBarProps) {
  const { t } = useTranslation();
  const { period, scope, today, staff, set } = filters;
  const teams = useScopeTeams();
  const users = useUsers();
  const projects = useProjects();
  const teamMembers = scope.teamId
    ? new Set(teams.find((team) => team.id === scope.teamId)?.members.map((m) => m.user_id))
    : undefined;
  const people = (users.data ?? []).filter(
    (u) => u.status !== "invited" && (!teamMembers || teamMembers.has(u.id)),
  );

  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <NativeSelect
        aria-label={t("scope.period")}
        value={period}
        onChange={(e) => {
          const next = e.target.value as Period;
          set(
            next === "custom"
              ? { period: next, from: scope.from, to: scope.to }
              : { period: next, from: undefined, to: undefined },
          );
        }}
      >
        {periods.map((p) => (
          <option key={p} value={p}>
            {t(`reports.periods.${p}`)}
          </option>
        ))}
      </NativeSelect>
      {period === "custom" && (
        <>
          <input
            type="date"
            aria-label={t("apps.from")}
            value={scope.from}
            max={today}
            onChange={(e) => e.target.value && set({ period: "custom", from: e.target.value })}
            className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
          />
          <span className="text-muted">—</span>
          <input
            type="date"
            aria-label={t("apps.to")}
            value={scope.to}
            onChange={(e) => e.target.value && set({ period: "custom", to: e.target.value })}
            className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
          />
        </>
      )}
      {staff && teams.length > 0 && (
        <NativeSelect
          aria-label={t("scope.team")}
          value={scope.teamId ?? ""}
          onChange={(e) => set({ team: e.target.value || undefined, user: undefined })}
        >
          <option value="">{t("scope.allTeams")}</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </NativeSelect>
      )}
      {staff && (
        <MultiSelect
          label={t("reports.filters.people")}
          allLabel={t("reports.filters.all")}
          options={people.map((u) => ({ value: u.id, label: u.full_name }))}
          value={scope.userIds ?? []}
          onChange={(user) => set({ user })}
        />
      )}
      {withProjects && (
        <MultiSelect
          label={t("reports.filters.projects")}
          allLabel={t("reports.filters.all")}
          options={(projects.data ?? []).map((p) => ({
            value: p.id,
            label: p.name,
            icon: (
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: p.color }}
                aria-hidden="true"
              />
            ),
          }))}
          value={scope.projectIds ?? []}
          onChange={(project) => set({ project })}
        />
      )}
    </div>
  );
}
